import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';

import { GroupTranslatorProvider } from '../../../components/i18n/GroupTranslationContext';
import { useToolbarItemTranslation } from '../hooks/useToolbarItemTranslation';

// Utils
import { editedOrSelectedElementsFromPropsSelector } from '../selector/onboardingToolbarSelector';

// Types
import { ToolbarItemConfig } from '../toolbarTypes';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { ImMNCurrentUser } from '../../../user/currentUserSelector';

const EmptyTool = () => <div />;

const activeElementSelector = createSelector(
    editedOrSelectedElementsFromPropsSelector,
    (elements): ImMNElement => elements?.first(),
);

const mapStateToProps = createStructuredSelector({
    // enrich the selectedElements passed as captured state from the tool list with the current state of those elements
    // this is necessary to ensure that the toolbar tools are always in sync with the current state of the elements
    selectedElements: editedOrSelectedElementsFromPropsSelector,
    element: activeElementSelector,
});

const connector = connect(mapStateToProps);

export type ToolbarItemsListRequiredProps = {
    selectedElementIds: Immutable.List<string>;
    currentUser: ImMNCurrentUser;
    currentUserId: string;
    currentBoardId: string;
    gridSize: number;
    gridSizeName: string;
    permissions: number;
    editorKey?: string;
    hasEditorSelection: boolean;
};

export type ToolbarItemsListProps = ConnectedProps<typeof connector> &
    ToolbarItemsListRequiredProps & {
        items: ToolbarItemConfig[];
        toolListId: string;
        getComponent: (item: ToolbarItemConfig) => React.ComponentType<Record<string, unknown>> | undefined;
    };

/**
 * Renders a list of toolbar tools/items, based on the provided configuration.
 */
const ToolbarItemWithTranslation = (props: {
    Component: React.ComponentType<Record<string, unknown>>;
    item: ToolbarItemConfig;
    rest: Record<string, unknown>;
}) => {
    const { Component, item, rest } = props;

    const { toolName } = useToolbarItemTranslation(item);

    return (
        // Spread rest first so item-provided name is overridden by toolName
        <Component {...rest} {...item} id={undefined} name={toolName} item={item}>
            {undefined}
        </Component>
    );
};

const TranslatedToolbarItem = (props: {
    Component: React.ComponentType<Record<string, unknown>>;
    item: ToolbarItemConfig;
    rest: Record<string, unknown>;
}) => {
    return (
        <GroupTranslatorProvider group="toolbar">
            <ToolbarItemWithTranslation {...props} />
        </GroupTranslatorProvider>
    );
};

const ToolbarItemsList = ({ items, getComponent, ...rest }: ToolbarItemsListProps): React.ReactElement => {
    return (
        <>
            {items.map((item) => {
                const Component = getComponent(item) || EmptyTool;

                return <TranslatedToolbarItem key={item.uid || item.id} {...{ Component, item, rest }} />;
            })}
        </>
    );
};

const memoisedToolbarItemsList = React.memo(
    ToolbarItemsList,
    (prevProps, nextProps) =>
        prevProps.toolListId === nextProps.toolListId &&
        prevProps.currentUser === nextProps.currentUser &&
        prevProps.selectedElementIds === nextProps.selectedElementIds &&
        prevProps.element === nextProps.element &&
        prevProps.editorKey === nextProps.editorKey &&
        prevProps.hasEditorSelection === nextProps.hasEditorSelection &&
        prevProps.currentBoardId === nextProps.currentBoardId &&
        prevProps.gridSize === nextProps.gridSize,
);

export default connector(memoisedToolbarItemsList);
